/**
 * App root
 *
 * Wraps the application with providers, routing, and navigation guards.
 * Includes optimized loading flows, analytics init, and Stripe auth handling.
 */
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { SidebarProvider } from './context/sidebar';
import { AuthProvider, useAuth } from './context/auth';
import { NamespaceProvider } from './context/namespace';
import { ToastProvider } from './components/ui/notifications/toast-notification.jsx';
import { HelmetProvider } from 'react-helmet-async';
import AuthLoadingScreen from './components/shared/AuthLoadingScreen';
import { smartPreload } from './utils/media/imagePreloader';
import { auth } from './firebase/firebase';
import { setupGlobalChunkErrorHandling } from './utils/errors/chunkErrorHandler';
import { isDefiniteBot, setupInteractionTracking } from './utils/navigation/botDetection';

// Auth guards
import { RequireAuth, RequireUnauth, RequireAuthVerification, RequireNonEnterprise } from './components/auth/guards';

// Navigation components
import { ScrollToTop, NavigationHandler, InitializeAuthNavigator } from './components/shared/navigation';

// Layout components
import { AppLayout } from './components/shared/layout';

// Auth components
import StripeAuthHandler from './components/auth/StripeAuthHandler';

// Page imports
import Landing from './pages/auth/landing/core/Landing.jsx';
import SignIn from './pages/auth/account/SignIn.jsx';
import SignUp from './pages/auth/account/SignUp.jsx';
import About from './pages/auth/landing/info/About.jsx';
import DoneForYou from './pages/auth/landing/offerings/DoneForYou.jsx';
import TermsOfService from './pages/auth/landing/legal/TermsOfService.jsx';
import PrivacyPolicy from './pages/auth/landing/legal/PrivacyPolicy.jsx';
import UsePolicy from './pages/auth/landing/legal/UsePolicy.jsx';
import DataProcessing from './pages/auth/landing/legal/DataProcessing.jsx';
import Templates from './pages/templates/Templates.jsx';
import Websites from './pages/templates/Websites.jsx';
import VideoTemplates from './pages/templates/VideoTemplates.jsx';
import StockVideos from './pages/templates/StockVideos.jsx';
import CreateCampaign from './pages/auth/testimonials/campaigns/CreateCampaign';
import ManageCampaigns from './pages/auth/testimonials/campaigns/ManageCampaigns';
import CampaignDetails from './pages/auth/testimonials/campaigns/CampaignDetails';
import EditProfile from './pages/account/EditProfile';
import Account from './pages/account/Account.jsx';
import Support from './pages/account/Support';
import Survey from './pages/auth/testimonials/survey/Survey.jsx';
import Responses from './pages/auth/testimonials/responses/Responses';
import CampaignSettings from './pages/auth/testimonials/campaigns/CampaignSettings';
import PricingPage from './pages/auth/checkout/PricingPage.jsx';
import CheckoutSuccess from './pages/auth/checkout/CheckoutSuccess.jsx';
import CheckoutCancel from './pages/auth/checkout/CheckoutCancel.jsx';
import CreateNamespace from './pages/namespaces/CreateNamespace.jsx';
import ManageNamespaces from './pages/namespaces/ManageNamespaces.jsx';
import EditNamespace from './pages/namespaces/EditNamespace.jsx';
import ForgotPassword from './pages/auth/password/ForgotPassword.jsx';
import UpdatePassword from './pages/auth/password/UpdatePassword.jsx';
import AuthActionRouter from './pages/auth/account/AuthActionRouter.jsx';
import CopyAnalyzer from './pages/tools/CopyAnalyzer.jsx';
import DoneForYouPage from './pages/tools/DoneForYou.jsx';
import SendText from './pages/tools/SendText.jsx';
import CreateTemplates from './pages/admin/templates/CreateTemplates.jsx';
import ManageTemplates from './pages/admin/templates/ManageTemplates.jsx';
import EditTemplate from './pages/admin/templates/EditTemplate.jsx';
import AdjustTags from './pages/admin/analytics/AdjustTags.jsx';
import TagCounts from './pages/admin/analytics/TagCounts.jsx';
import UserManagement from './pages/admin/users/UserManagement.jsx';
import Admin from './pages/admin/core/Admin.jsx';
import AdminTemplates from './pages/admin/templates/AdminTemplates.jsx';
import AdminUsers from './pages/admin/users/AdminUsers.jsx';
import UsersList from './pages/admin/users/UsersList.jsx';
import AdminClaims from './pages/admin/users/AdminClaims.jsx';
import AdminEnterprises from './pages/admin/enterprises/AdminEnterprises.jsx';
import AdminTools from './pages/admin/core/AdminTools.jsx';
import AdminStockVideos from './pages/admin/videos/AdminStockVideos.jsx';
import CreateStockVideo from './pages/admin/videos/CreateStockVideo.jsx';
import EditStockVideo from './pages/admin/videos/EditStockVideo.jsx';
import ManageStockVideos from './pages/admin/videos/ManageStockVideos.jsx';
import CreateVideoTemplate from './pages/admin/videos/CreateVideoTemplate.jsx';
import EditVideoTemplate from './pages/admin/videos/EditVideoTemplate.jsx';
import ManageVideoTemplates from './pages/admin/videos/ManageVideoTemplates.jsx';
import CreateWebsite from './pages/admin/websites/CreateWebsite.jsx';
import EditWebsite from './pages/admin/websites/EditWebsite.jsx';
import ManageWebsites from './pages/admin/websites/ManageWebsites.jsx';
import DemoTemplates from './pages/auth/landing/offerings/DemoTemplates.jsx';
import FacebookAdLanding from './pages/auth/landing/core/FacebookAdLanding.jsx';
import AccountPricingPage from './pages/account/AccountPricingPage.jsx';
import FreeUsers from './pages/admin/users/FreeUsers.jsx';
import EnterpriseSignUp from './pages/auth/enterprise/EnterpriseSignUp.jsx';
import EnterpriseSignIn from './pages/auth/enterprise/EnterpriseSignIn.jsx';
import ManageEnterprises from './pages/admin/enterprises/ManageEnterprises.jsx';
import CreateEnterprise from './pages/admin/enterprises/CreateEnterprise.jsx';
import EditEnterprise from './pages/admin/enterprises/EditEnterprise.jsx';
import ManageEnterprise from './pages/admin/enterprises/ManageEnterprise.jsx';
import EmailVerificationPending from './pages/auth/verification/EmailVerificationPending.jsx';
import CustomerLists from './pages/admin/users/CustomerLists.jsx';
import Analytics from './pages/admin/analytics/Analytics.jsx';
import StaticTemplatesPage from './pages/templates/StaticTemplatesPage.jsx';
import MailTemplatesAdmin from './pages/admin/mail/MailTemplatesAdmin.jsx';
import CreateMailTemplate from './pages/admin/mail/CreateMailTemplate.jsx';
import { MaintenanceBanner } from './components/ui/notifications/maintenance-banner.jsx';
import { AnnouncementBanner } from './components/ui/notifications/announcement-banner.jsx';

/**
 * AppWithLoading - Main application wrapper with initial loading screen
 */
function AppWithLoading() {
  const [initialLoading, setInitialLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [additionalImagesLoaded, setAdditionalImagesLoaded] = useState(false);
  
  // Bypass the initial splash screen on lightweight public routes that shouldn't preload
  const isFacebookAdPage = window.location.pathname === '/ad/facebook';
  const isPublicPricingPage = window.location.pathname === '/pricing';
  
  // Effect for controlling the initial loading
  useEffect(() => {
    // Skip the heavy image-preload splash for Facebook ad or public pricing pages
    if (isFacebookAdPage || isPublicPricingPage) {
      setInitialLoading(false);
      return;
    }
    
    // OPTIMIZATION: For landing page (likely unauthenticated users), skip heavy image preloading
    const isLikelyLandingPageVisit = window.location.pathname === '/' && 
                                    !localStorage.getItem('stripe_checkout_initiated') &&
                                    !sessionStorage.getItem('justLoggedIn') &&
                                    !sessionStorage.getItem('googleSignInInProgress');
    
    if (isLikelyLandingPageVisit) {
      console.log('🚀 Using ultra-optimized landing page preloader for public visit...');
      const timer = setTimeout(() => {
        setImagesLoaded(true);
        setMinTimeElapsed(true);
      }, 150);
      return () => clearTimeout(timer);
    }
    
    // Use smart preloading strategy for authenticated/returning users
    const startPreloading = async () => {
      try {
        console.log('Starting smart image preloading...');
        await smartPreload(
          (criticalImages) => {
            console.log('Critical images loaded, can start app:', criticalImages.length);
            setImagesLoaded(true);
          },
          (loaded, total) => {
            console.log(`Additional images loading: ${loaded}/${total}`);
            if (loaded === total) {
              console.log('All additional images loaded for instant switching');
              setAdditionalImagesLoaded(true);
            }
          }
        );
        console.log('Smart preloading completed');
      } catch (error) {
        console.error('Error in smart preloading:', error);
        setImagesLoaded(true);
      }
    };
    
    startPreloading();
    
    const minTimeTimer = setTimeout(() => {
      console.log('Minimum loading time elapsed');
      setMinTimeElapsed(true);
    }, 800);
    
    return () => clearTimeout(minTimeTimer);
  }, [isFacebookAdPage, isPublicPricingPage]);
  
  // Only hide loading when both conditions are met
  useEffect(() => {
    if (isFacebookAdPage || isPublicPricingPage) return;
    
    if (imagesLoaded && minTimeElapsed) {
      console.log('All loading conditions met, hiding loading screen');
      setInitialLoading(false);
    }
  }, [imagesLoaded, minTimeElapsed, isFacebookAdPage, isPublicPricingPage]);
  
  // Track additional images for authenticated users
  useEffect(() => {
    if (additionalImagesLoaded) {
      console.log('🚀 All landing page images preloaded - switching will be instant!');
    }
  }, [additionalImagesLoaded]);
  
  if (initialLoading) {
    console.log('RENDERING LOADER: AppWithLoading initialLoading');
    return <AuthLoadingScreen />;
  }
  
  return (
    <BrowserRouter>
      <HelmetProvider>
        <AuthProvider>
          <NamespaceProvider>
            <SidebarProvider>
              <ToastProvider>
                <MainApp />
              </ToastProvider>
            </SidebarProvider>
          </NamespaceProvider>
        </AuthProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
}

/**
 * AppContent - Landing page content with auth-based redirects
 */
function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const isFromSignup = location.state?.from?.pathname === '/signup';
  
  // Track user interaction to distinguish from bots
  useEffect(() => {
    return setupInteractionTracking();
  }, []);
  
  // Effect to handle authenticated user redirects
  useEffect(() => {
    if (location.pathname === '/ad/facebook') {
      console.log('AppContent: On Facebook ad page, skipping all redirects');
      return;
    }
    
    if (user && !loading && location.pathname === '/') {
      console.log('AppContent: Authenticated user detected, proceeding with redirect');
      
      if (sessionStorage.getItem('accountDeletionInProgress') === 'true') {
        console.log('Account deletion in progress, skipping AppContent redirect');
        return;
      }
      
      const redirectDelay = 100;
      setIsRedirecting(true);
      
      const isEnterpriseUser = user.isEnterpriseUser === true || user.isEnterprise === true;
      
      if (isEnterpriseUser && auth.currentUser && !auth.currentUser.emailVerified) {
        console.log('AppContent: Enterprise user needs email verification, redirecting to verify-email page');
        setTimeout(() => navigate('/verify-email', { replace: true }), redirectDelay);
        return;
      }
      
      const isPremiumOrPro = user.planLevel === 'premium' || user.isEnterprise;
      
      const timer = setTimeout(() => {
        let destination;
        
        if (isEnterpriseUser) {
          destination = '/app/';
          console.log('AppContent: Enterprise user detected, redirecting to app');
        } else if (isPremiumOrPro) {
          destination = '/app/';
          console.log('AppContent: Premium user detected, redirecting to app');
        } else {
          destination = '/pricing';
          console.log('AppContent: Basic user detected, redirecting to public pricing');
        }
        
        navigate(destination, { replace: true });
      }, redirectDelay);
      
      return () => clearTimeout(timer);
    }
    
    // Handle unauthenticated users
    if (!user && !loading && location.pathname === '/') {
      console.log('AppContent: Unauthenticated user on root path, checking for auth errors...');
      
      if (isDefiniteBot()) {
        console.log('AppContent: Obvious bot/crawler detected, skipping redirects for SEO');
        return;
      }
      
      // Check for very recent Google sign-in errors
      const googleSignInError = sessionStorage.getItem('google_signin_error') === 'true';
      const googleSignInTimestamp = sessionStorage.getItem('google_signin_error_timestamp');
      
      if (googleSignInError && googleSignInTimestamp) {
        const errorAge = Date.now() - parseInt(googleSignInTimestamp);
        if (errorAge < 15000) {
          console.log('AppContent: Very recent Google sign-in error detected, redirecting to sign-in page');
          setTimeout(() => navigate('/signin', { replace: true }), 300);
          return;
        } else {
          console.log('AppContent: Stale Google sign-in error, clearing it');
          sessionStorage.removeItem('google_signin_error');
          sessionStorage.removeItem('google_signin_error_timestamp');
        }
      } else if (googleSignInError) {
        sessionStorage.removeItem('google_signin_error');
        sessionStorage.removeItem('google_signin_error_timestamp');
      }
      
      // Check for any stored auth errors
      const authError = localStorage.getItem('refrral_auth_error');
      const authErrorTimestamp = localStorage.getItem('auth_error_timestamp');
      
      if (authError && authErrorTimestamp) {
        const errorAge = Date.now() - parseInt(authErrorTimestamp);
        if (errorAge < 15000) {
          console.log('AppContent: Very recent auth error detected, redirecting to sign-in page');
          setTimeout(() => navigate('/signin', { replace: true }), 300);
          return;
        } else {
          console.log('AppContent: Found stale auth error, clearing it instead of redirecting');
          localStorage.removeItem('refrral_auth_error');
          localStorage.removeItem('auth_error_timestamp');
          const authErrorKeys = ['auth_error', 'stripe_auth_error', 'refrral_auth_error_key', 'auth_error_storage_key'];
          authErrorKeys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          });
        }
      } else if (authError || authErrorTimestamp) {
        console.log('AppContent: Found incomplete auth error data, clearing stale data');
        localStorage.removeItem('refrral_auth_error');
        localStorage.removeItem('auth_error_timestamp');
      }
      
      console.log('AppContent: Clean session detected, allowing normal access to home page');
      sessionStorage.removeItem('google_signin_error');
      sessionStorage.removeItem('google_signin_error_timestamp');
      localStorage.removeItem('refrral_auth_error');
      localStorage.removeItem('auth_error_timestamp');
    }
  }, [user, loading, location.pathname, navigate, isFromSignup]);

  if (loading || isRedirecting) {
    return <AuthLoadingScreen />;
  }

  if (user && location.pathname === '/') {
    return <AuthLoadingScreen />;
  }

  return <Landing />;
}

/**
 * MainApp - Main application with routes
 */
function MainApp() {
  const { user, loading, isProfileLoaded, isAdmin, lastError, isTransitioning } = useAuth(); 
  const location = useLocation();

  // Initialize analytics and error handling when app starts
  useEffect(() => {
    setupGlobalChunkErrorHandling();
  }, []);

  // Track page changes for analytics
  useEffect(() => {
    const pageName = location.pathname === '/' ? 'home' : location.pathname.replace('/', '');
    if (window.advocacyLabAnalytics) {
      window.advocacyLabAnalytics.currentPage = pageName;
      console.log(`📍 Page changed to: ${pageName}`);
    }
  }, [location.pathname]);

  // OPTIMIZED: Special handling for survey pages and Facebook ad page
  const isSurveyPage = location.pathname.startsWith('/survey/');
  const isFacebookAdPage = location.pathname === '/ad/facebook';

  // Simplified loading condition
  const googleSignInInProgress = sessionStorage.getItem('googleSignInInProgress') === 'true';
  const googleSignUpInProgress = sessionStorage.getItem('googleSignUpInProgress') === 'true';
  const hasAuthError = localStorage.getItem('refrral_auth_error') !== null;
  const isPublicPricing = location.pathname === '/pricing';

  const showLoadingScreen = !isSurveyPage &&
    !isFacebookAdPage &&
    !hasAuthError &&
    loading &&
    !isPublicPricing &&
    (user && !isProfileLoaded && location.pathname !== '/new-signup') &&
    !googleSignInInProgress && !googleSignUpInProgress && !isTransitioning;

  // Debugging logs
  useEffect(() => {
    console.log('App.jsx - Auth State:', { 
      user, loading, isProfileLoaded, isAdmin, lastError,
      path: location.pathname, isSurveyPage,
      googleSignInInProgress, googleSignUpInProgress,
      hasAuthError, isTransitioning
    });
  }, [user, loading, isProfileLoaded, isAdmin, lastError, location.pathname, isSurveyPage, googleSignInInProgress, googleSignUpInProgress, hasAuthError, isTransitioning]);

  if (showLoadingScreen) {
    return <AuthLoadingScreen />;
  }

  return (
    <>
      <AnnouncementBanner />
      <MaintenanceBanner />
      <ScrollToTop />
      <NavigationHandler />
      <InitializeAuthNavigator />
      <StripeAuthHandler />
      <Routes>
        <Route path="/survey/:id" element={<Survey />} />
        <Route path="/" element={<AppContent />} />
        <Route path="/about" element={<About />} />
        <Route path="/done-for-you" element={<DoneForYou />} />
        <Route path="/demo-templates" element={<DemoTemplates />} />
        <Route path="/ad/facebook" element={<FacebookAdLanding />} />
        <Route path="/tos" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/aup" element={<UsePolicy />} />
        <Route path="/dpa" element={<DataProcessing />} />
        {/* Redirect legacy legal slugs to the standardized paths used across all FullPAC sites */}
        <Route path="/terms" element={<Navigate to="/tos" replace />} />
        <Route path="/use-policy" element={<Navigate to="/aup" replace />} />
        <Route path="/templates" element={<StaticTemplatesPage />} />
        <Route path="/login" element={<RequireUnauth><SignIn /></RequireUnauth>} />
        <Route path="/signin" element={<RequireUnauth><SignIn /></RequireUnauth>} />
        <Route path="/forgot-password" element={<RequireUnauth><ForgotPassword /></RequireUnauth>} />
        <Route path="/auth-action" element={<AuthActionRouter />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/signup" element={<RequireUnauth><SignUp /></RequireUnauth>} />
        <Route path="/enterprise/signup" element={<RequireUnauth><EnterpriseSignUp /></RequireUnauth>} />
        <Route path="/enterprise/signin" element={<RequireUnauth><EnterpriseSignIn /></RequireUnauth>} />
        <Route path="/verify-email" element={<RequireAuthVerification><EmailVerificationPending /></RequireAuthVerification>} />
        <Route path="/pricing" element={<RequireNonEnterprise><PricingPage /></RequireNonEnterprise>} />
        <Route path="/success" element={<CheckoutSuccess />} />
        <Route path="/cancel" element={<CheckoutCancel />} />
        <Route path="/app" element={<RequireAuth><AppLayout /></RequireAuth>}>
          <Route index element={<Templates />} />
          <Route path="websites" element={<Websites />} />
          <Route path="videoTemplates" element={<VideoTemplates />} />
          <Route path="stockVideos" element={<StockVideos />} />
          <Route path="copyAnalyzer" element={<CopyAnalyzer />} />
          <Route path="doneForYou" element={<DoneForYouPage />} />
          <Route path="sendText" element={<SendText />} />
          <Route path="campaigns/new" element={<CreateCampaign />} />
          <Route path="campaigns/:id/responses" element={<Responses />} />
          <Route path="campaigns/:id/settings" element={<CampaignSettings />} />
          <Route path="campaigns/:id" element={<CampaignDetails />} />
          <Route path="campaigns" element={<ManageCampaigns />} />
          <Route path="profile" element={<EditProfile />} />
          <Route path="account" element={<Account />} />
          <Route path="account/pricing" element={<AccountPricingPage />} />
          <Route path="support" element={<Support />} />
          <Route path="namespaces/create" element={<CreateNamespace />} />
          <Route path="namespaces/manage" element={<ManageNamespaces />} />
          <Route path="namespaces/edit/:id" element={<EditNamespace />} />
          <Route path="private/admin" element={<Admin />} />
          <Route path="private/admin/templates" element={<AdminTemplates />} />
          <Route path="private/admin/mail-templates" element={<MailTemplatesAdmin />} />
          <Route path="private/admin/create-mail-template" element={<CreateMailTemplate />} />
          <Route path="private/admin/stock-videos" element={<AdminStockVideos />} />
          <Route path="private/admin/users" element={<AdminUsers />} />
          <Route path="private/admin/users-list" element={<UsersList />} />
          <Route path="private/admin/claims" element={<AdminClaims />} />
          <Route path="private/admin/customer-lists" element={<CustomerLists />} />
          <Route path="private/admin/enterprises" element={<AdminEnterprises />} />
          <Route path="private/admin/tools" element={<AdminTools />} />
          <Route path="private/createTemplate" element={<CreateTemplates />} />
          <Route path="private/manageTemplates" element={<ManageTemplates />} />
          <Route path="private/editTemplate/:templateId" element={<EditTemplate />} />
          <Route path="private/createStockVideo" element={<CreateStockVideo />} />
          <Route path="private/editStockVideo/:id" element={<EditStockVideo />} />
          <Route path="private/manageStockVideos" element={<ManageStockVideos />} />
          <Route path="private/createVideoTemplate" element={<CreateVideoTemplate />} />
          <Route path="private/editVideoTemplate/:videoTemplateId" element={<EditVideoTemplate />} />
          <Route path="private/manageVideoTemplates" element={<ManageVideoTemplates />} />
          <Route path="private/createWebsite" element={<CreateWebsite />} />
          <Route path="private/editWebsite/:websiteId" element={<EditWebsite />} />
          <Route path="private/manageWebsites" element={<ManageWebsites />} />
          <Route path="private/adjustTags" element={<AdjustTags />} />
          <Route path="private/tagCounts" element={<TagCounts />} />
          <Route path="private/userManagement" element={<UserManagement />} />
          <Route path="private/freeUsers" element={<FreeUsers />} />
          <Route path="private/manageEnterprises" element={<ManageEnterprises />} />
          <Route path="private/createEnterprise" element={<CreateEnterprise />} />
          <Route path="private/editEnterprise/:id" element={<EditEnterprise />} />
          <Route path="private/manageEnterprise/:id" element={<ManageEnterprise />} />
          <Route path="private/analytics" element={<Analytics />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return <AppWithLoading />;
}

export default App;
